import { FolderTree, Box, ShieldCheck, ScanLine } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useProjects, useRepos, useWhoami } from '../api/client';
import { useFindings } from '../api/detection';
import { FORMATS, formatCaveat, isExperimentalFormat } from '../lib/formats';
import { Card, CardBody, CardHeader } from '../components/ui/Card';

function openFindingsLabel(openFindings: unknown[] | undefined): string | number {
  if (!openFindings) return '—';
  if (openFindings.length === 1000) return '1000+';
  return openFindings.length;
}

function FormatTile({ label, slug, formatId, experimental, caveat }: Readonly<{ label: string; slug: string | null; formatId: string; experimental?: boolean; caveat?: string }>) {
  // SVGs are vendored under public/format-icons/ by
  // scripts/sync-format-icons.mjs. The glyph is rendered as a CSS
  // mask-image on a <span> so its colour follows the surrounding
  // text-fg-muted → group-hover:text-accent gradient — `<img>` would
  // render the SVG's default-black fill and ignore the CSS colour.
  // No CDN round-trip means the dashboard works air-gapped (item 24)
  // and never falls back to a generic Box because of a transient
  // cdn.simpleicons.org 5xx.
  const { t } = useTranslation();
  const showLogo = !!slug;
  return (
    <Link
      to={`/repos?format=${encodeURIComponent(formatId)}&new=1`}
      className="flex flex-col items-center gap-2 p-3 rounded-md border border-border bg-bg-inset/40 hover:bg-bg-hover/40 hover:border-accent/60 hover:shadow-glow-accent transition-all group"
      title={
        caveat
          ? t('home.createRepoCaveat', { label, tag: experimental ? t('home.experimentalTag') : t('home.noteTag'), caveat })
          : t('home.createRepo', { label })
      }
    >
      <div className="h-8 w-8 flex items-center justify-center text-fg-muted group-hover:text-accent transition-colors">
        {showLogo ? (
          <span
            aria-hidden="true"
            className="h-7 w-7 inline-block bg-current"
            style={{
              WebkitMaskImage: `url(/format-icons/${slug}.svg)`,
              maskImage: `url(/format-icons/${slug}.svg)`,
              WebkitMaskRepeat: 'no-repeat',
              maskRepeat: 'no-repeat',
              WebkitMaskPosition: 'center',
              maskPosition: 'center',
              WebkitMaskSize: 'contain',
              maskSize: 'contain',
            }}
          />
        ) : (
          <Box className="h-5 w-5" />
        )}
      </div>
      <span className="text-[11px] text-fg text-center leading-tight tabular-nums group-hover:text-accent transition-colors">
        {label}
      </span>
    </Link>
  );
}

export function Home() {
  const { t } = useTranslation();
  const { user } = useWhoami();
  const { data: projects } = useProjects();
  const { data: repos } = useRepos();
  // v161 — open findings count for the dashboard tile. We pull a
  // capped list (limit=1000) to compute the count clientside; a
  // dedicated count endpoint can land later if the UI needs the
  // exact number above 1000.
  const { data: openFindings } = useFindings({ status: 'open', limit: 1000 });
  const displayName = user ? (user.displayName ?? user.email.split('@')[0]) : undefined;

  const stats = [
    {
      Icon: FolderTree,
      label: t('home.stats.projects.label'),
      hint: t('home.stats.projects.hint'),
      value: projects?.length ?? '—',
      to: '/projects',
    },
    {
      Icon: Box,
      label: t('home.stats.repositories.label'),
      hint: t('home.stats.repositories.hint'),
      value: repos?.length ?? '—',
      to: '/repos',
    },
    {
      Icon: ScanLine,
      label: t('home.stats.openFindings.label'),
      hint: t('home.stats.openFindings.hint'),
      value: openFindingsLabel(openFindings),
      to: '/detection/findings?status=open',
    },
    {
      Icon: ShieldCheck,
      label: t('home.stats.trustBundles.label'),
      hint: t('home.stats.trustBundles.hint'),
      value: 7,
      to: '/admin/trust',
    },
  ];

  return (
    <div className="space-y-8">
      <div>
        <p className="orb-eyebrow text-accent/80 mb-2">{t('home.eyebrow')}</p>
        <h1 className="text-3xl font-display font-semibold text-fg leading-tight">
          {displayName ? t('home.welcomeNamed', { name: displayName }) : t('home.welcome')}
        </h1>
        <p className="text-fg-muted mt-3 max-w-3xl leading-relaxed">
          <Trans
            i18nKey="home.intro"
            values={{ count: FORMATS.length }}
            components={[<span key={0} className="font-semibold text-fg" />, <span key={1} className="text-fg" />]}
          />
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 items-stretch">
        {stats.map(({ Icon, label, hint, value, to }) => {
          const card = (
            <Card className="h-full">
              <CardBody className="flex items-start gap-3 h-full">
                <div className="h-10 w-10 rounded-md bg-accent/10 border border-accent/30 flex items-center justify-center shrink-0">
                  <Icon className="h-5 w-5 text-accent" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-2xl font-display font-semibold text-fg leading-none tabular-nums">
                    {value}
                  </div>
                  <div className="orb-section-title mt-1.5">{label}</div>
                  <div className="text-[11px] text-fg-muted mt-0.5 leading-snug">
                    {hint}
                  </div>
                </div>
              </CardBody>
            </Card>
          );
          return to ? (
            <Link key={label} to={to} className="block h-full hover:opacity-90 transition-opacity">
              {card}
            </Link>
          ) : (
            <div key={label} className="h-full">{card}</div>
          );
        })}
      </div>

      <Card>
        <CardHeader
          title={t('home.formatAdapters', { count: FORMATS.length })}
          description={t('home.formatAdaptersDescription')}
        />
        <CardBody>
          <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-7 lg:grid-cols-9 gap-3">
            {FORMATS.map((f) => (
              <FormatTile
                key={f.id}
                label={f.label}
                slug={f.slug}
                formatId={f.id}
                experimental={isExperimentalFormat(f)}
                caveat={formatCaveat(f)}
              />
            ))}
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
